import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Check, Users, ChevronDown, Loader2, AlertCircle } from "lucide-react";
import { BACKEND_URL } from "../lib/backend";
import { useLanguage } from "../hooks/useLanguage";

// Analytics helper (PostHog is loaded via index.html)
const track = (event, props = {}) => {
  try {
    if (typeof window !== "undefined" && window.posthog) {
      window.posthog.capture(event, props);
    }
  } catch (_e) {
    /* analytics failures must never break UX */
  }
};

const USER_TYPES = [
  { value: "dialysis_patient", label_en: "Dialysis patient", label_ro: "Pacient dializat", label_fr: "Patient dialysé" },
  { value: "transplant_patient", label_en: "Kidney transplant patient", label_ro: "Pacient cu transplant renal", label_fr: "Patient transplanté rénal" },
  { value: "ckd", label_en: "Living with CKD", label_ro: "Trăiesc cu BCR", label_fr: "Vivant avec l'IRC" },
  { value: "caregiver", label_en: "Caregiver", label_ro: "Îngrijitor", label_fr: "Aidant" },
  { value: "healthcare_professional", label_en: "Healthcare professional", label_ro: "Profesionist medical", label_fr: "Professionnel de santé" },
];

const DEVICES = [
  { value: "iphone", label_en: "iPhone", label_ro: "iPhone", label_fr: "iPhone" },
  { value: "android", label_en: "Android", label_ro: "Android", label_fr: "Android" },
  { value: "both", label_en: "Both", label_ro: "Ambele", label_fr: "Les deux" },
];

const LANGS = [
  { value: "ro", label_en: "Romanian", label_ro: "Română", label_fr: "Roumain" },
  { value: "en", label_en: "English", label_ro: "Engleză", label_fr: "Anglais" },
  { value: "fr", label_en: "French", label_ro: "Franceză", label_fr: "Français" },
];

const COPY = {
  en: {
    title: "Join the Nephrix Private Beta",
    description: "Be among the first people to test Nephrix and help shape a better digital companion for the kidney community.",
    firstName: "First name",
    email: "Email address",
    country: "Country",
    userType: "I am a…",
    device: "My device",
    language: "Preferred language",
    gdpr: "I agree that Nephrix may use the information provided to contact me regarding beta testing. I have read the",
    privacyLink: "Privacy Policy",
    submit: "Join the Beta",
    submitting: "Submitting…",
    successTitle: "Thank you for joining the Nephrix community.",
    successBody: "We will contact you when a beta testing place becomes available.",
    errorGeneric: "We couldn't submit your registration. Please try again in a moment.",
    errorDuplicate: "This email is already registered for the beta.",
    errorRate: "Too many submissions from your network. Please try again later.",
    fieldRequired: "Required",
    selectPlaceholder: "Select…",
    counter: "people already on the beta list",
  },
  ro: {
    title: "Înscrie-te în Beta privat Nephrix",
    description: "Fii printre primii oameni care testează Nephrix și ajută-ne să construim un companion digital mai bun pentru comunitatea renală.",
    firstName: "Prenume",
    email: "Adresă email",
    country: "Țară",
    userType: "Sunt…",
    device: "Dispozitivul meu",
    language: "Limba preferată",
    gdpr: "Sunt de acord ca Nephrix să folosească informațiile furnizate pentru a mă contacta în legătură cu testarea beta. Am citit",
    privacyLink: "Politica de Confidențialitate",
    submit: "Înscrie-mă în Beta",
    submitting: "Se trimite…",
    successTitle: "Mulțumim că te alături comunității Nephrix.",
    successBody: "Te vom contacta când se va elibera un loc în programul de testare beta.",
    errorGeneric: "Nu am putut trimite înscrierea. Te rugăm să încerci din nou peste puțin timp.",
    errorDuplicate: "Această adresă de email este deja înscrisă în beta.",
    errorRate: "Prea multe încercări din rețeaua ta. Te rugăm să încerci mai târziu.",
    fieldRequired: "Obligatoriu",
    selectPlaceholder: "Selectează…",
    counter: "persoane deja pe lista beta",
  },
  fr: {
    title: "Rejoignez la Bêta Privée Nephrix",
    description: "Soyez parmi les premières personnes à tester Nephrix et à façonner un meilleur compagnon numérique pour la communauté rénale.",
    firstName: "Prénom",
    email: "Adresse email",
    country: "Pays",
    userType: "Je suis…",
    device: "Mon appareil",
    language: "Langue préférée",
    gdpr: "J'accepte que Nephrix utilise les informations fournies pour me contacter concernant le test bêta. J'ai lu la",
    privacyLink: "Politique de Confidentialité",
    submit: "Rejoindre la Bêta",
    submitting: "Envoi…",
    successTitle: "Merci de rejoindre la communauté Nephrix.",
    successBody: "Nous vous contacterons dès qu'une place de test bêta sera disponible.",
    errorGeneric: "Impossible d'envoyer votre inscription. Veuillez réessayer dans un instant.",
    errorDuplicate: "Cette adresse email est déjà inscrite à la bêta.",
    errorRate: "Trop de tentatives depuis votre réseau. Veuillez réessayer plus tard.",
    fieldRequired: "Obligatoire",
    selectPlaceholder: "Sélectionner…",
    counter: "personnes déjà inscrites en bêta",
  },
};

const inputBase =
  "w-full px-4 py-3 bg-slate-900/60 border border-slate-700 rounded-xl text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-400/20 transition-colors";

const BetaSignupForm = () => {
  const { currentLang } = useLanguage();
  const c = COPY[currentLang] || COPY.en;

  const [form, setForm] = useState({
    first_name: "",
    email: "",
    country: "",
    user_type: "",
    device: "",
    preferred_language: currentLang || "en",
    gdpr_consent: false,
  });
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error
  const [errorMessage, setErrorMessage] = useState("");
  const [count, setCount] = useState(0);
  const [startedTracked, setStartedTracked] = useState(false);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/beta-signup/count`)
      .then((r) => r.json())
      .then((d) => setCount(d.count || 0))
      .catch(() => setCount(0));
  }, []);

  const update = (field) => (e) => {
    const val = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    if (!startedTracked) {
      track("beta_form_started");
      setStartedTracked(true);
    }
    setForm((f) => ({ ...f, [field]: val }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (status === "submitting") return;
    setStatus("submitting");
    setErrorMessage("");

    try {
      const res = await fetch(`${BACKEND_URL}/api/beta-signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json().catch(() => ({}));

      if (res.status === 429) {
        setStatus("error");
        setErrorMessage(c.errorRate);
        track("beta_form_failed", { reason: "rate_limit" });
        return;
      }

      if (!res.ok) {
        setStatus("error");
        setErrorMessage(data.detail || c.errorGeneric);
        track("beta_form_failed", { reason: data.detail || "server_error", status: res.status });
        return;
      }

      if (data.success === false) {
        setStatus("error");
        setErrorMessage(c.errorDuplicate);
        track("beta_form_failed", { reason: "duplicate" });
        return;
      }

      setStatus("success");
      setCount((n) => n + 1);
      track("beta_form_submitted", {
        user_type: form.user_type,
        device: form.device,
        preferred_language: form.preferred_language,
      });
    } catch (_err) {
      setStatus("error");
      setErrorMessage(c.errorGeneric);
      track("beta_form_failed", { reason: "network" });
    }
  };

  const label = (t, key = "label_en") => t[`label_${currentLang}`] || t[key];

  return (
    <section
      id="beta"
      className="py-24 md:py-32 bg-slate-950"
      data-testid="beta-signup-section"
    >
      <div className="max-w-3xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-10"
        >
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-teal-500/10 text-teal-300 text-xs font-semibold tracking-wide uppercase border border-teal-400/20">
            <Users className="w-3.5 h-3.5" /> Private Beta
          </span>
          <h2 className="mt-6 text-4xl md:text-5xl font-bold text-slate-50 tracking-tight" style={{ fontFamily: "Manrope, sans-serif" }}>
            {c.title}
          </h2>
          <p className="mt-4 text-base md:text-lg text-slate-400 leading-relaxed">
            {c.description}
          </p>
          {count > 0 && (
            <p className="mt-4 text-sm text-slate-500">
              <span className="text-teal-300 font-semibold">{count.toLocaleString()}</span> {c.counter}
            </p>
          )}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="bg-slate-900/60 border border-slate-800 rounded-2xl p-6 md:p-10 backdrop-blur"
        >
          {status === "success" ? (
            <div className="text-center py-10" data-testid="beta-success">
              <div className="w-16 h-16 rounded-full bg-teal-500/20 border border-teal-400/40 flex items-center justify-center mx-auto mb-6">
                <Check className="w-8 h-8 text-teal-300" />
              </div>
              <h3 className="text-2xl md:text-3xl font-bold text-slate-50 mb-3">{c.successTitle}</h3>
              <p className="text-slate-400 max-w-md mx-auto">{c.successBody}</p>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="beta-form">
              <div>
                <label htmlFor="first_name" className="block text-sm font-medium text-slate-300 mb-2">
                  {c.firstName} <span className="text-teal-400">*</span>
                </label>
                <input
                  id="first_name"
                  type="text"
                  required
                  maxLength={80}
                  autoComplete="given-name"
                  aria-required="true"
                  value={form.first_name}
                  onChange={update("first_name")}
                  className={inputBase}
                  data-testid="beta-first-name"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-slate-300 mb-2">
                  {c.email} <span className="text-teal-400">*</span>
                </label>
                <input
                  id="email"
                  type="email"
                  required
                  autoComplete="email"
                  aria-required="true"
                  value={form.email}
                  onChange={update("email")}
                  className={inputBase}
                  data-testid="beta-email"
                />
              </div>

              <div>
                <label htmlFor="country" className="block text-sm font-medium text-slate-300 mb-2">
                  {c.country} <span className="text-teal-400">*</span>
                </label>
                <input
                  id="country"
                  type="text"
                  required
                  maxLength={80}
                  autoComplete="country-name"
                  aria-required="true"
                  value={form.country}
                  onChange={update("country")}
                  className={inputBase}
                  data-testid="beta-country"
                />
              </div>

              <div>
                <label htmlFor="user_type" className="block text-sm font-medium text-slate-300 mb-2">
                  {c.userType} <span className="text-teal-400">*</span>
                </label>
                <div className="relative">
                  <select
                    id="user_type"
                    required
                    aria-required="true"
                    value={form.user_type}
                    onChange={update("user_type")}
                    className={`${inputBase} appearance-none pr-10`}
                    data-testid="beta-user-type"
                  >
                    <option value="" disabled>{c.selectPlaceholder}</option>
                    {USER_TYPES.map((u) => (
                      <option key={u.value} value={u.value}>{label(u)}</option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                </div>
              </div>

              <div>
                <label htmlFor="device" className="block text-sm font-medium text-slate-300 mb-2">
                  {c.device} <span className="text-teal-400">*</span>
                </label>
                <div className="relative">
                  <select
                    id="device"
                    required
                    aria-required="true"
                    value={form.device}
                    onChange={update("device")}
                    className={`${inputBase} appearance-none pr-10`}
                    data-testid="beta-device"
                  >
                    <option value="" disabled>{c.selectPlaceholder}</option>
                    {DEVICES.map((d) => (
                      <option key={d.value} value={d.value}>{label(d)}</option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                </div>
              </div>

              <div>
                <label htmlFor="preferred_language" className="block text-sm font-medium text-slate-300 mb-2">
                  {c.language} <span className="text-teal-400">*</span>
                </label>
                <div className="relative">
                  <select
                    id="preferred_language"
                    required
                    aria-required="true"
                    value={form.preferred_language}
                    onChange={update("preferred_language")}
                    className={`${inputBase} appearance-none pr-10`}
                    data-testid="beta-language"
                  >
                    {LANGS.map((l) => (
                      <option key={l.value} value={l.value}>{label(l)}</option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                </div>
              </div>

              <div className="md:col-span-2">
                <label className="flex items-start gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    required
                    aria-required="true"
                    checked={form.gdpr_consent}
                    onChange={update("gdpr_consent")}
                    className="mt-1 w-4 h-4 accent-teal-500 flex-shrink-0"
                    data-testid="beta-gdpr"
                  />
                  <span className="text-sm text-slate-400 leading-relaxed">
                    {c.gdpr}{" "}
                    <Link to="/privacy" className="text-teal-300 hover:text-teal-200 underline underline-offset-2">
                      {c.privacyLink}
                    </Link>.
                  </span>
                </label>
              </div>

              {status === "error" && (
                <div
                  className="md:col-span-2 flex items-start gap-3 p-4 rounded-xl bg-red-500/10 border border-red-500/30"
                  role="alert"
                  data-testid="beta-error"
                >
                  <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-200">{errorMessage}</p>
                </div>
              )}

              <div className="md:col-span-2">
                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="w-full flex items-center justify-center gap-2 py-4 px-8 bg-teal-500 hover:bg-teal-400 disabled:opacity-60 disabled:cursor-not-allowed text-slate-950 font-bold rounded-xl transition-colors text-base tracking-wide"
                  data-testid="beta-submit"
                >
                  {status === "submitting" ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {c.submitting}
                    </>
                  ) : (
                    c.submit
                  )}
                </button>
              </div>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
};

export default BetaSignupForm;
